import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
  Collapse,
  Card,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const navigate = useNavigate();

  // Extract token from URL when component mounts.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("token");
    if (resetToken) {
      setToken(resetToken);
    }
  }, []);

  // Password strength checker.
  const checkPasswordStrength = (pwd) => {
    if (pwd.length === 0) return "";
    if (pwd.length < 6) return "Weak";
    if (pwd.length < 10) return "Medium";
    return "Strong";
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const getStrengthColor = () => {
    if (passwordStrength === "Weak") return "#f44336";
    if (passwordStrength === "Medium") return "#ff9800";
    if (passwordStrength === "Strong") return "#4caf50";
    return "#e0e0e0";
  };

  const getStrengthWidth = () => {
    if (passwordStrength === "Weak") return "33%";
    if (passwordStrength === "Medium") return "66%";
    if (passwordStrength === "Strong") return "100%";
    return "0%";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    try {
      const { status } = await axios.patch(
        "https://le-travaille-server.onrender.com/auth/reset-password",
        {
          token,
          newPassword: password,
        },
      );

      if (status === 200) {
        toast.success("Password reset successful!");
        navigate("/auth/success");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 450 }}
      >
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #003d38 0%, #00786F 100%)",
              color: "white",
              textAlign: "center",
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,215,0,0.15)",
                  borderRadius: "50%",
                  padding: "16px",
                  display: "inline-block",
                  mb: 2,
                  border: "2px solid rgba(255,215,0,0.2)",
                }}
              >
                <LockResetIcon sx={{ fontSize: 40, color: "#FFD700" }} />
              </Box>
            </motion.div>

            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Reset Password
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              Enter your new password below
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Create a new password that you haven't used before.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <TextField
                  label="New Password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={handlePasswordChange}
                  type={showPassword ? "text" : "password"}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 120, 111, 0.04)",
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },
                    mb: 1,
                  }}
                />

                {/* Password Strength Bar */}
                {password && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Password strength:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: getStrengthColor(), fontWeight: 600 }}
                      >
                        {passwordStrength}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: 4,
                        backgroundColor: "#e0e0e0",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: getStrengthWidth(),
                          height: "100%",
                          backgroundColor: getStrengthColor(),
                          transition:
                            "width 0.3s ease, background-color 0.3s ease",
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <TextField
                  label="Confirm Password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 120, 111, 0.04)",
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },
                    mb: 2,
                  }}
                />
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Collapse in={Boolean(error)}>
                    <Alert
                      severity="error"
                      action={
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => setError("")}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      {error}
                    </Alert>
                  </Collapse>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#00786F",
                    color: "#fff",
                    fontWeight: 600,
                    py: 1.8,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#005b54",
                      transform: "scale(1.02)",
                      boxShadow: "0 4px 16px rgba(0, 120, 111, 0.3)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(0, 120, 111, 0.5)",
                    },
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </motion.div>
            </Box>
          </Box>
        </Card>
      </motion.div>
    </Box>
  );
};

export default ResetPassword;
